import * as pc from 'playcanvas';
import { POWER_UP_DEFS, type PowerUpType } from '../shared';
import { matCache } from '../utils/MaterialCache';

export class PowerUpEntity {
  entity: pc.Entity;
  private boxMesh: pc.Entity;
  private orbMesh: pc.Entity;
  private hpBarBg: pc.Entity;
  private hpBarFill: pc.Entity;
  private boxMat: pc.StandardMaterial;
  private orbMat: pc.StandardMaterial;
  private color: [number, number, number];
  private time: number = 0;
  private phase: 'box' | 'orb' = 'box';
  private boxRotY: number = 0;

  constructor(app: pc.Application, id: string, type: PowerUpType, x: number, z: number) {
    const def = POWER_UP_DEFS[type];
    const [r, g, b] = def.color;
    this.color = def.color;

    this.entity = new pc.Entity(`powerup_${id}`);
    app.root.addChild(this.entity);
    this.entity.setPosition(x, 0, z);

    // ── Box mesh (visible in 'box' phase) ──────────────────
    this.boxMesh = new pc.Entity('boxMesh');
    this.boxMesh.addComponent('render', { type: 'box' });
    // createUnique: emissive is mutated in updateHp() for the hit-flash effect
    this.boxMat = matCache.createUnique({
      diffuse: new pc.Color(r * 0.25, g * 0.25, b * 0.25),
      emissive: new pc.Color(r, g, b),
      emissiveIntensity: 0.7,
    });
    this.boxMesh.render!.meshInstances[0].material = this.boxMat;
    this.boxMesh.setLocalScale(1.2, 1.2, 1.2);
    this.boxMesh.setLocalPosition(0, 0.6, 0);
    this.entity.addChild(this.boxMesh);

    // ── Orb mesh (visible in 'orb' phase) ──────────────────
    this.orbMesh = new pc.Entity('orbMesh');
    this.orbMesh.addComponent('render', { type: 'sphere' });
    // createUnique: emissiveIntensity is mutated every frame for the pulse effect
    this.orbMat = matCache.createUnique({
      diffuse: new pc.Color(r * 0.2, g * 0.2, b * 0.2),
      emissive: new pc.Color(r, g, b),
      emissiveIntensity: 1.2,
    });
    this.orbMesh.render!.meshInstances[0].material = this.orbMat;
    this.orbMesh.setLocalScale(0.8, 0.8, 0.8);
    this.orbMesh.setLocalPosition(0, 0.6, 0);
    this.orbMesh.enabled = false;  // hidden until phase === 'orb'
    this.entity.addChild(this.orbMesh);

    // ── HP bar (box phase only) ─────────────────────────────
    this.hpBarBg = new pc.Entity('hpBg');
    this.hpBarBg.addComponent('render', { type: 'plane' });
    this.hpBarBg.render!.meshInstances[0].material = matCache.get({ diffuse: new pc.Color(0.1, 0.05, 0.05) });
    this.hpBarBg.render!.castShadows = false;
    this.hpBarBg.render!.receiveShadows = false;
    this.hpBarBg.setLocalScale(1.6, 1, 0.1);
    this.hpBarBg.setLocalPosition(0, 1.4, 0);
    this.entity.addChild(this.hpBarBg);

    this.hpBarFill = new pc.Entity('hpFill');
    this.hpBarFill.addComponent('render', { type: 'plane' });
    // get(): fill color is set once from def color and only scale/position change at runtime
    this.hpBarFill.render!.meshInstances[0].material = matCache.get({
      diffuse: new pc.Color(r, g, b),
      emissive: new pc.Color(r, g, b),
      emissiveIntensity: 1.0,
    });
    this.hpBarFill.render!.castShadows = false;
    this.hpBarFill.render!.receiveShadows = false;
    this.hpBarFill.setLocalScale(1, 1, 0.85);
    this.hpBarFill.setLocalPosition(0, 0.002, 0);
    this.hpBarBg.addChild(this.hpBarFill);
  }

  setPhase(phase: 'box' | 'orb') {
    if (this.phase === phase) return;
    this.phase = phase;
    this.time = 0;
    if (phase === 'orb') {
      this.boxMesh.enabled = false;
      this.orbMesh.enabled = true;
      this.hpBarBg.enabled = false;
    } else {
      this.boxMesh.enabled = true;
      this.orbMesh.enabled = false;
      this.hpBarBg.enabled = true;
    }
  }

  updateHp(hp: number, maxHp: number) {
    if (this.phase !== 'box') return;
    const pct = maxHp > 0 ? Math.max(0, hp / maxHp) : 1;
    this.hpBarFill.setLocalScale(pct, 1, 0.85);
    this.hpBarFill.setLocalPosition((pct - 1) * 0.5, 0.002, 0);
    // Flash red on hit
    const [r, g, b] = this.color;
    this.boxMat.emissive = new pc.Color(1, 0.15, 0.15);
    this.boxMat.update();
    setTimeout(() => {
      this.boxMat.emissive = new pc.Color(r, g, b);
      this.boxMat.update();
    }, 120);
  }

  update(dt: number, cameraPos: pc.Vec3) {
    this.time += dt;

    if (this.phase === 'box') {
      // Slow rotation + gentle float
      this.boxRotY += 60 * dt;
      this.boxMesh.setLocalEulerAngles(0, this.boxRotY, 0);
      const bob = 0.6 + Math.sin(this.time * 1.8) * 0.12;
      this.boxMesh.setLocalPosition(0, bob, 0);
      this.hpBarBg.setLocalPosition(0, bob + 0.8, 0);
      // Billboard HP bar toward camera
      const pos = this.entity.getPosition();
      const dx = cameraPos.x - pos.x;
      const dz = cameraPos.z - pos.z;
      const yaw = Math.atan2(dx, dz) * (180 / Math.PI);
      this.hpBarBg.setLocalEulerAngles(90, yaw, 0);
    } else {
      // Faster rotation + bob
      const angles = this.orbMesh.getLocalEulerAngles();
      this.orbMesh.setLocalEulerAngles(angles.x, angles.y + 90 * dt, angles.z);
      const bob = 0.6 + Math.sin(this.time * 2.5) * 0.15;
      this.orbMesh.setLocalPosition(0, bob, 0);
      // Pulse emissive intensity
      this.orbMat.emissiveIntensity = 1.0 + Math.sin(this.time * 3) * 0.4;
      this.orbMat.update();
    }
  }

  destroy() {
    this.entity.destroy();
  }
}
